import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import Presenter from './Presenter';
import XLSX from 'xlsx';
import withReactContent from 'sweetalert2-react-content';

import { axiosCustom, getFormatDate } from '../../../common/common.function';
import { IMemberProps, WakeUp } from '../../../common/interface/props';
import Table from '../../UI/atoms/Table';

const MySwal = withReactContent(Swal);

export default function Container({ solType }: IMemberProps) {
  const { register, handleSubmit, getValues } = useForm<{
    searchType: string;
    searchValue: string;
  }>({});

  // 시작일 (기본값 : 현재일 - 7일)
  const [startDate, setStartDate] = useState(
    (() => {
      const d = new Date();
      d.setDate(d.getDate() - 60);
      return d;
    })(),
  );

  // 종료일 (기본값 : 현재일)
  const [endDate, setEndDate] = useState(new Date());

  // 페이지당 노출 수
  const [viewCount, setViewCount] = useState(30);

  // 현재 페이지
  const [nowPage, setNowPage] = useState(0);

  // 작업 총 수
  const [max, setMax] = useState(0);

  // 작업 데이터
  const [jobData, setJobData] = useState([]);

  // IP 검사
  useEffect(() => {
    async function ip() {
      try {
        await axiosCustom.get(`${process.env.REACT_APP_BACKEND_ENDPOINT}/api/member/${solType.toLowerCase()}/ipCheck`);
      } catch (error: any) {
        await Swal.fire({
          title: '실패',
          html: error.message,
          icon: 'error',
        });

        window.location.href = 'https://plto.com';
      }
    }
    ip();
  }, []);

  useEffect(() => {
    nowPage !== 0 && getJobData();
  }, [nowPage]);

  function onSubmit() {
    nowPage === 1 ? getJobData() : setNowPage(1);
  }

  // 검색 이벤트
  async function getJobData() {
    const data = getValues();

    Swal.fire({
      title: '잠시만 기다려 주세요.',
      html: '작업 조회중 ...', // add html attribute if you want or remove
      allowOutsideClick: false,
      showConfirmButton: false,
      onBeforeOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const { result } = await axiosCustom.get(`${process.env.REACT_APP_BACKEND_ENDPOINT}/api/job/admin/detail-search`, {
        params: {
          startDate: getFormatDate(startDate),
          endDate: getFormatDate(endDate) + ' 23:59:59',
          solType: solType,
          searchType: data.searchType,
          searchValue: data.searchValue,
          viewCount,
          nowPage,
        },
      });
      if (result.jobData.length <= 0 || result.totalCount === 0) {
        Swal.fire({
          title: '실패',
          html: `정보가 조회되지 않습니다.`,
          icon: 'error',
        });
      } else {
        // 작업 데이터 설정
        setJobData(result.jobData);

        // 최대 페이지 설정
        setMax(Math.ceil(result.totalCount / viewCount));

        Swal.close();
      }
    } catch (error: any) {
      Swal.fire({
        title: '실패',
        html: `${error.message}`,
        icon: 'error',
      });
    }
  }

  // 작업 상세 보기/엑셀 다운로드
  async function jobDetailEvent(type: 'EXCEL' | 'VIEW', jobId: number, count: number) {
    if (type === 'VIEW' && count > 100) {
      Swal.fire({
        title: '경고',
        html: '100 개 이상 조회는 엑셀 다운로드를 이용 부탁 드립니다',
        icon: 'error',
      });
      return false;
    }

    Swal.fire({
      title: '잠시만 기다려 주세요.',
      html: '작업 상세 다운로드 ...', // add html attribute if you want or remove
      allowOutsideClick: false,
      showConfirmButton: false,
      onBeforeOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const { result: jobDetailData } = await axiosCustom.get(`${process.env.REACT_APP_BACKEND_ENDPOINT}/api/job/admin/detail-job/${jobId}`, {
        timeout: 300000,
      });

      if (type === 'VIEW') {
        MySwal.fire({
          width: 1200,
          animation: false,
          title: '작업 상세 정보',
          html: (
            <>
              <Table
                headerItems={Object.keys(jobDetailData[0]).map((one, index) => ({
                  width:
                    index === 0
                      ? 110
                      : index === 1
                      ? 130
                      : index === 2
                      ? 160
                      : index === 3
                      ? 160
                      : index === 4
                      ? 220
                      : index === 5
                      ? 200
                      : index === 6
                      ? 220
                      : index === 7
                      ? 180
                      : index === 8
                      ? 230
                      : index === 9
                      ? 160
                      : index === 10
                      ? 220
                      : 100,
                  align: 'center',
                  headerAlign: 'center',
                  headerName: one,
                  editable: true,
                }))}
                bodyItems={jobDetailData.map((prodOne: any) => Object.keys(prodOne).map((one) => prodOne[one]))}
              />
            </>
          ),
          confirmButtonText: '확인',
        });
      } else if (type === 'EXCEL') {
        const dataWS = XLSX.utils.json_to_sheet(jobDetailData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, dataWS, 'jobDetailData');
        XLSX.writeFile(wb, `${jobId}.xlsx`);
        Swal.close();
      }
    } catch (error: any) {
      Swal.fire({
        title: '실패',
        html: `${error.message}`,
        icon: 'error',
      });
    }
  }

  return (
    <Presenter
      useForm={{ register, handleSubmit }}
      onSubmit={onSubmit}
      solType={solType}
      state={{ startDate, endDate, jobData, nowPage, max }}
      setState={{ setStartDate, setEndDate, setNowPage }}
      jobDetailEvent={jobDetailEvent}
    />
  );
}
